import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  CalendarAgendaItem,
  CalendarGrouping,
  CalendarSession,
} from 'api/calendar/types';
import { ScheduleViewTabs } from 'components/molecules/ScheduleViewTabs';
import useIsMobile from 'hooks/useIsMobile';
import { RowDisplay } from './scheduleView.types';
import DaySection from './DaySection';
import DayStrip from './DayStrip';
import ScheduleDatePicker from './ScheduleDatePicker';
import ScheduleFilters from './ScheduleFilters';
import ScheduleHero from './ScheduleHero';
import useScheduleFilter from './useScheduleFilter';
import useResolvedSurface from './useResolvedSurface';

interface ScheduleViewProps {
  /** The consecutive days currently loaded, oldest first (the first is the selected/anchor day). */
  readonly loadedDates: ReadonlyArray<string>;
  readonly selectedDate: string;
  readonly locale: string;
  readonly grouping: CalendarGrouping;
  readonly categoryIds: ReadonlyArray<string>;
  readonly vibeIds: ReadonlyArray<string>;
  readonly inventory: Record<string, boolean>;
  readonly onSelectDate: (date: string) => void;
  readonly onBook: (item: CalendarAgendaItem, session: CalendarSession) => void;
  /** A day finished loading — the page may now append the next day if the sentinel is in view. */
  readonly onDayLoaded: (date: string) => void;
  /** Sentinel observed for infinite scroll: appends the next day when it nears the viewport. */
  readonly sentinelRef: React.Ref<HTMLDivElement>;
  /** Rows with MORE than this many sessions collapse into a per-row dropdown (defaults to 4).
   *  Pass `null` to disable collapsing entirely — every session renders as its own inline row. */
  readonly collapseThreshold?: number | null;
  readonly categoryFilter?: boolean;
  readonly vibeFilter?: boolean;
  readonly rowDisplay?: RowDisplay;
  /** Distance (px) from the top where the sticky nav + headings begin sticking. */
  readonly stickyTopOffset?: number;
}

const DEFAULT_COLLAPSE_THRESHOLD = 4;

/* True when the embed is running inside an iframe (as on partner sites). The agenda's
   `min-height: calc(100vh - 200px)` floor keeps the standalone page tall enough for the sticky
   nav to pin, but inside an iframe `100vh` resolves to the iframe's own height — which a partner
   auto-resizer grows to fit content, feeding a loop that never converges. Drop the floor when
   framed so the agenda is purely content-sized (like the listings page, which never grows). */
const isEmbeddedInIframe = (() => {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin/sandboxed frames can throw on window.top access — treat as framed.
    return true;
  }
})();

const ScheduleView = ({
  loadedDates,
  selectedDate,
  locale,
  grouping,
  categoryIds,
  vibeIds,
  inventory,
  onSelectDate,
  onBook,
  onDayLoaded,
  sentinelRef,
  collapseThreshold = DEFAULT_COLLAPSE_THRESHOLD,
  categoryFilter = true,
  vibeFilter = false,
  rowDisplay = { image: true, duration: true, spotsRemaining: true },
  stickyTopOffset = 0,
}: ScheduleViewProps) => {
  const { t: translate } = useTranslation();
  // Combined category/vibe filter (one dropdown). selectedLabel names the active filter for the
  // empty state ("No {Category} experiences scheduled").
  const filter = useScheduleFilter({
    categoryEnabled: categoryFilter,
    vibeEnabled: vibeFilter,
  });

  // On mobile the explicit Book CTA is hidden and the whole row taps to book (per design); gate
  // the row's interactivity on this so it isn't a nested-interactive control on desktop.
  const isMobile = useIsMobile();

  // The opaque surface color the schedule paints with: the client's page background when they
  // set one, otherwise white. Applied to Page so the sticky bars inherit an opaque color and
  // occlude scrolling content even when the host page has no background of its own.
  const surface = useResolvedSurface();

  // Keep the sticky stack in sync so each layer pins right below the one above it:
  //   • Browse|Schedule tabs stick at the configured stickyTopOffset (their own component).
  //   • Day-strip nav stacks at --schedule-sticky-offset (offset + tabs height, if tabs present).
  //   • Date/group headings stack at --schedule-sticky-top (nav offset + nav height).
  // A single ResizeObserver watches both the tabs (may be hidden by config) and the nav so
  // breakpoint changes (nav wrapping to two rows, tabs hidden on narrow widths, etc.) update the
  // stacked offsets together.
  const innerRef = useRef<HTMLDivElement | null>(null);
  const navRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const inner = innerRef.current;
    const nav = navRef.current;
    if (!inner || !nav) return undefined;
    const offset = Math.max(0, stickyTopOffset);
    const apply = () => {
      const tabs = inner.querySelector<HTMLElement>(
        '.schedule-view__tabs-sticky',
      );
      const tabsHeight = tabs ? tabs.offsetHeight : 0;
      const navOffset = offset + tabsHeight;
      inner.style.setProperty('--schedule-sticky-offset', `${navOffset}px`);
      inner.style.setProperty(
        '--schedule-sticky-top',
        `${navOffset + nav.offsetHeight}px`,
      );
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(nav);
    const tabs = inner.querySelector<HTMLElement>(
      '.schedule-view__tabs-sticky',
    );
    if (tabs) observer.observe(tabs);
    window.addEventListener('resize', apply);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', apply);
    };
  }, [stickyTopOffset]);

  return (
    <PageRoot className="schedule-view" style={{ background: surface }}>
      <ScheduleHero />
      <PageContent>
        <Inner ref={innerRef} className="schedule-view__inner">
          <StickyTabs
            className="schedule-view__tabs-sticky"
            style={{ top: `${Math.max(0, stickyTopOffset)}px` }}
          >
            <ScheduleViewTabs active="schedule" />
          </StickyTabs>

          {/* One sticky nav row: date picker (left) · scrollable day strip (center, hidden on
            mobile) · combined filter (right). */}
          <StickyNav
            ref={navRef}
            className="schedule-view__nav"
            role="region"
            aria-label={translate('scheduleNav', {
              defaultValue: 'Schedule navigation',
            })}
          >
            <NavRow className="schedule-view__nav-row">
              <ScheduleDatePicker
                selectedDate={selectedDate}
                locale={locale}
                onSelectDate={onSelectDate}
              />

              <DayStrip
                selectedDate={selectedDate}
                locale={locale}
                inventory={inventory}
                onSelectDate={onSelectDate}
              />

              <ScheduleFilters
                enabled={filter.enabled}
                options={filter.options}
                selectedValues={filter.selectedValues}
                onChange={filter.onChange}
              />
            </NavRow>
          </StickyNav>

          {/* Vertical list of consecutive day sections; each owns one calendar request and appends
            as the sentinel nears the viewport (infinite scroll across days). aria-live "polite"
            so screen readers announce newly-appended days; "additions" limits noise. */}
          <AgendaList
            className="schedule-view__agenda"
            style={isEmbeddedInIframe ? { minHeight: 'auto' } : undefined}
            aria-live="polite"
            aria-relevant="additions"
            aria-label={translate('scheduleAgenda', {
              defaultValue: 'Schedule agenda',
            })}
          >
            {loadedDates.map((date) => (
              <DaySection
                key={date}
                date={date}
                locale={locale}
                grouping={grouping}
                categoryIds={categoryIds}
                vibeIds={vibeIds}
                collapseThreshold={collapseThreshold}
                rowDisplay={rowDisplay}
                filterLabel={filter.selectedLabel}
                isMobile={isMobile}
                onBook={onBook}
                onLoaded={onDayLoaded}
              />
            ))}
            {/* Sentinel: when it scrolls into view the page appends the next day. */}
            <Sentinel ref={sentinelRef} aria-hidden="true" />
          </AgendaList>
        </Inner>
      </PageContent>
    </PageRoot>
  );
};

export default ScheduleView;

/* Outer surface: carries the client's page background so the hero (edge-to-edge) and the padded
   content below share the same backdrop. No horizontal padding here — the hero paints all the
   way to the viewport edges, matching the landing page's hero placement. */
const PageRoot = styled.div`
  font-family: inherit;
  display: flex;
  flex-direction: column;
  background: inherit;
`;

/* Padded content column beneath the hero — holds the sticky tabs/nav and the agenda list. Top
   padding matches the landing page's Wrapper `padding-top: 12px`; combined with the hero's own
   `margin-bottom: 62px` (see ScheduleHero), the visible hero-to-content gap ends up at the same
   ~74px the landing page has (62 + 12). Side padding stays wider than the landing Wrapper's on
   purpose — the schedule agenda is a narrower-feeling column by design. */
const PageContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 16px 56px;
  background: inherit;

  @media all and (min-width: 768px) {
    padding: 12px 120px 42px;
  }
`;

const Inner = styled.div`
  width: 100%;
  max-width: 1200px;
  /* Keep the client background flowing down so the sticky nav/headings below inherit an
     opaque color (rather than transparent) and occlude scrolling content. */
  background: inherit;
  /* Updated at runtime to the pinned nav's height so date/group headings stack just beneath it. */
  --schedule-sticky-top: 96px;
`;

const StickyTabs = styled.div`
  position: sticky;
  z-index: 2;
  /* Opaque via the inherited client background so scrolling agenda content is occluded. */
  background: inherit;
`;

const StickyNav = styled.div`
  position: sticky;
  top: var(--schedule-sticky-offset, 0px);
  z-index: 2;
  /* Opaque via the inherited client background so scrolling agenda content is occluded. */
  background: inherit;
  padding-top: 8px;
`;

/* One sticky nav row: calendar picker (left) · scrollable day strip (center) · filter (right).
   Bottom border matches the shared Divider component (`components/common/styled/common-styled`)
   used between the categories header and the featured/listings grid, so the visual break under
   the day strip reads as the same separator on both surfaces. */
const NavRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 12px 0 16px;
  border-bottom: 1px solid var(--way-palette-black-10);

  @media all and (min-width: 768px) {
    gap: 16px;
    padding: 16px 0 24px;
  }
`;

/* ---- agenda ---- */
const AgendaList = styled.div`
  position: relative;
  width: 100%;
  /* Pass the client background through to each day's sticky DateHeading. */
  background: inherit;
  /* Ensure the document stays tall enough that scrollToScheduleTop can always pin the sticky
     nav at the viewport top — otherwise selecting a new date (which resets to a single day
     section) lets the document shrink, the browser clamps the scroll position toward the page
     top, and the user briefly sees the hero before the next day loads back in.
     Overridden to min-height:auto inline when embedded in an iframe (see isEmbeddedInIframe)
     to avoid the vh-driven auto-resize feedback loop on partner sites. */
  min-height: calc(100vh - 200px);
`;

/* Invisible IntersectionObserver target at the bottom of the list — appends the next day. */
const Sentinel = styled.div`
  width: 100%;
  height: 1px;
`;
